/**
 * API client.
 *
 * The session lives in an HttpOnly cookie, which JavaScript cannot read - that is
 * the point of it. So there is no token to store and no Authorization header to
 * build; `credentials: 'same-origin'` is the whole mechanism.
 *
 * State-changing requests need the CSRF token echoed in a header. That token is in
 * a readable cookie precisely so this code can read it, which is what makes the
 * double-submit check meaningful: an attacker on another origin can cause the
 * browser to send the session cookie but cannot read the CSRF cookie to match it.
 */

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
}

/** `/auth/me` returns permissions alongside the user rather than nested in it. */
export interface Session {
  readonly user: SessionUser;
  readonly permissions: readonly string[];
}

export interface Summary {
  readonly billed: string;
  readonly grossProfit: string;
  readonly hours: string;
  readonly weeks: number;
  readonly contractors: number;
  readonly clients: number;
  readonly recruiters: number;
  readonly activeHeadcount: number;
  readonly firstWeek: string | null;
  readonly lastWeek: string | null;
  readonly attribution: readonly {
    readonly source: string;
    readonly isReal: boolean;
    readonly assignments: number;
    readonly grossProfit: string;
  }[];
}

export interface WorkforceComposition {
  readonly w2: number;
  readonly contractor1099: number;
  readonly internal: number;
}

export interface CommissionPlanSchedule {
  readonly id: string;
  readonly name: string;
  readonly fillPriority: number;
  readonly isElevated: boolean;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly rates: readonly { tierId: string; rate: string }[];
}

export interface CommissionPlan {
  readonly id: string;
  readonly name: string;
  readonly tierMode: string;
  readonly elevatedPeriodMonths: number;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly activeAllocations: number;
  readonly brackets: readonly { id: string; threshold: string }[];
  readonly schedules: readonly CommissionPlanSchedule[];
}

export interface WeeklyCommissionData {
  readonly weekEnding: string | null;
  readonly recruiters: readonly WeeklyRecruiter[];
  readonly companyTotal: { gp: string; hours: string; commission: string; contractors: number };
  readonly priorWeek: { weekEnding: string; gp: string; hours: string; contractors: number } | null;
  readonly companyTrend: readonly {
    weekEnding: string;
    gp: string;
    hours: string;
    contractors: number;
  }[];
  readonly weeks: readonly string[];
  readonly tierMode: string;
  readonly tiers: readonly { threshold: string; rate: string }[];
}

export interface GpSummaryCustomer {
  readonly clientId: string;
  readonly name: string;
  readonly billed: string;
  readonly loadedCost: string;
  readonly expenses: string;
  readonly subVendor: string;
  readonly netGp: string;
  readonly hours: string;
  readonly contractors: number;
  readonly margin: string;
}

export interface GpSummaryData {
  readonly weekEnding: string | null;
  readonly customers: readonly GpSummaryCustomer[];
  readonly companyTotal: {
    readonly billed: string;
    readonly loadedCost: string;
    readonly expenses: string;
    readonly subVendor: string;
    readonly netGp: string;
    readonly hours: string;
    readonly customers: number;
    readonly contractors: number;
    readonly margin: string;
    readonly gpPerHour: string | null;
  } | null;
  readonly priorWeek: {
    readonly weekEnding: string;
    readonly gp: string;
    readonly billed: string;
    readonly hours: string;
  } | null;
  readonly weeks: readonly string[];
  readonly trend: readonly {
    readonly weekEnding: string;
    readonly gp: string;
    readonly billed: string;
    readonly hours: string;
  }[];
}

export interface ScoreboardRanking {
  readonly rank: number;
  readonly userId: string;
  readonly name: string;
  readonly role: string;
  readonly weeklyGp: string;
  readonly weeklyDelta: string | null;
  readonly ytdGp: string;
  readonly contractors: number;
  readonly hours: string;
  readonly sparkline: readonly number[];
  readonly tierPosition: {
    readonly tierIndex: number;
    readonly totalTiers: number;
    readonly rate: string;
    readonly pct: number;
    readonly nextTierGap: string | null;
  } | null;
}

export interface WeeklyScoreboardData {
  readonly weekEnding: string | null;
  readonly priorWeekEnding: string | null;
  readonly rankings: readonly ScoreboardRanking[];
  readonly companyGp: string;
  readonly weeks: readonly string[];
  readonly tiers: readonly { readonly threshold: string; readonly rate: string }[];
}

export interface GoalWeekRow {
  readonly weekNumber: number;
  readonly weekEnding: string;
  readonly isHoliday: boolean;
  readonly weeklyTarget: string;
  readonly actual: string | null;
  readonly variance: string | null;
  readonly ytd: string;
  readonly ytdAverage: string | null;
  readonly goalRemaining: string;
  readonly needToAverage: string | null;
}

export interface GoalProgress {
  readonly annualAmount: string;
  readonly asOfWeekNumber: number;
  readonly asOfWeekEnding: string;
  readonly weeklyActual: string | null;
  readonly weeklyTarget: string;
  readonly weeklyVariance: string | null;
  readonly ytd: string;
  readonly ytdAverage: string | null;
  readonly ytdGoal: string;
  readonly pointToPoint: string;
  readonly goalRemaining: string;
  readonly weeksRemaining: number;
  readonly needToAverage: string | null;
  readonly weeks: readonly GoalWeekRow[];
}

export interface GoalRanking {
  readonly userId: string;
  readonly name: string;
  readonly role: string;
  readonly weeklyGp: string;
  readonly ytdGp: string;
}

export interface GoalPortalData {
  readonly missingPlan: boolean;
  readonly year: number;
  readonly subject: string;
  readonly userId: string | null;
  readonly progress: GoalProgress | null;
  readonly rankings: readonly GoalRanking[];
  readonly notes: readonly {
    readonly id: string;
    readonly body: string;
    readonly kind: string;
    readonly author: string | null;
    readonly createdAt: string;
  }[];
  readonly milestones: readonly { readonly kind: string; readonly label: string }[];
  readonly weeks: readonly string[];
}

export interface ExpenseRow {
  readonly id: string;
  readonly assignmentId: string;
  readonly workerName: string;
  readonly clientName: string;
  readonly amount: string;
  readonly description: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

export interface ExpensesReportData {
  readonly weekEnding: string | null;
  readonly expenses: readonly ExpenseRow[];
  readonly total: string;
  readonly weeks: readonly string[];
}

export interface WeeklyRecruiter {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly grossProfit: string;
  readonly hours: string;
  readonly commission: string;
  readonly contractorCount: number;
  readonly gpShare: string;
  readonly priorWeekGp: string;
  readonly gpDelta: string | null;
  readonly commissionGpRatio: string;
  readonly ytdGp: string;
  readonly tierPosition: {
    tierIndex: number;
    totalTiers: number;
    rate: string;
    pct: number;
    nextTierGap: string | null;
  } | null;
  readonly sparkline: readonly number[];
  readonly contractors: readonly {
    workerId: string;
    name: string;
    clientName: string;
    hours: string;
    payRate: string | null;
    billRate: string | null;
    loadFactor: string | null;
    subVendorRate: string | null;
    gpPerHour: string | null;
    grossProfit: string;
    share: string;
    allocatedGp: string;
  }[];
}

export interface TrendPoint {
  readonly weekEnding: string;
  readonly billed: string;
  readonly grossProfit: string;
  readonly hours: string;
  readonly contractors: number;
}

export interface ClientRow {
  readonly id: string;
  readonly name: string;
  readonly contractors: number;
  readonly billed: string;
  readonly grossProfit: string;
  readonly hours: string;
  readonly realAttributions: number;
  readonly demoAttributions: number;
  readonly paymentTerms: string | null;
}

export interface ContractorRow {
  readonly id: string;
  readonly name: string;
  readonly workerType: string;
  readonly clientName: string;
  readonly attributionSource: string;
  readonly weeks: number;
  readonly hours: string;
  readonly billed: string;
  readonly grossProfit: string;
  readonly grossProfitPerHour: string | null;
  readonly payRate: string | null;
  readonly billRate: string | null;
  readonly loadFactor: string | null;
}

export interface RecruiterRow {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly planId: string | null;
  readonly planName: string | null;
  readonly contractors: number;
  readonly grossProfit: string;
  readonly hours: string;
  readonly commission: string;
  readonly statedAttributions: number;
  readonly fabricatedAttributions: number;
}

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export interface MyCommissionData {
  readonly recruiter: { readonly id: string; readonly name: string; readonly role: string };
  readonly ytd: {
    readonly grossProfit: string;
    readonly hours: string;
    readonly contractorCount: number;
    readonly commission: string;
    readonly currentTierRate: string;
    readonly currentTierIndex: number;
    readonly nextTierGap: string | null;
    readonly tierMode: string;
    readonly gpPerHour: string | null;
  };
  readonly thisWeek: {
    readonly weekEnding: string;
    readonly grossProfit: string;
    readonly hours: string;
    readonly contractors: number;
  } | null;
  readonly priorWeek: {
    readonly weekEnding: string;
    readonly grossProfit: string;
    readonly hours: string;
    readonly contractors: number;
  } | null;
  readonly tiers: readonly { readonly threshold: string; readonly rate: string }[];
  readonly contractors: readonly {
    readonly workerId: string;
    readonly name: string;
    readonly clientName: string;
    readonly share: string;
    readonly hours: string;
    readonly grossProfit: string;
    readonly gpPerHour: string | null;
  }[];
  readonly trend: readonly {
    readonly weekEnding: string;
    readonly grossProfit: string;
    readonly hours: string;
  }[];
}

function dateQuery(range?: DateRange): string {
  if (range === undefined) return '';
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

// ── Admin types ─────────────────────────────────────────────────────────

export interface SyncResultEntry {
  readonly source: string;
  readonly entity: string;
  readonly created: number;
  readonly updated: number;
  readonly skipped: number;
  readonly errors: readonly { ref: string; message: string }[];
}

export interface SyncResponse {
  readonly results: readonly SyncResultEntry[];
}

export interface OverduePeriod {
  readonly id: string;
  readonly weekEnding: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly daysOverdue: number;
}

export interface OverduePeriodsResponse {
  readonly overdue: readonly OverduePeriod[];
  readonly count: number;
}

export interface AdminPeriodRow {
  readonly id: string;
  readonly weekEnding: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly timeEntryCount: number;
  readonly gpRowCount: number;
  readonly adpSubmissionCount: number;
  readonly createdAt: string;
}

export interface PeriodGapRow {
  readonly afterPeriodId: string | null;
  readonly gapStart: string;
  readonly gapEnd: string;
  readonly gapDays: number;
}

export interface PeriodConfigResponse {
  readonly payWeekEndingDay: number;
  /** The payroll switchover. Weeks ending before it are never overdue; null until set. */
  readonly payrollGoLiveDate: string | null;
  /** Minimum expected billable hours per week before flagging. */
  readonly verifyMinHours: number;
  /** Whether automatic hour flagging is enabled. */
  readonly verifyFlaggingEnabled: boolean;
}

export interface SyncRunRow {
  readonly id: string;
  readonly source: string;
  readonly entity: string;
  readonly status: string;
  readonly recordsCreated: number;
  readonly recordsUpdated: number;
  readonly recordsSkipped: number;
  readonly errorDetails: unknown;
  readonly startedAt: string;
  readonly completedAt: string | null;
}

// Vite injects BASE_URL from the `base` config at build time.
// Dev: '/rmc/', Production: '/'. Strip the trailing slash for fetch paths.
const BASE = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

/**
 * Build a query string for pipeline endpoints. Accepts either a payrollRunId
 * or a periodId — the API's resolveRunContext accepts both and prefers
 * payrollRunId when present.
 */
function pq(id?: string, paramName: 'payrollRunId' | 'periodId' = 'periodId'): string {
  return id ? `?${paramName}=${encodeURIComponent(id)}` : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('rmc_csrf');
    if (csrf !== null) headers.set('x-csrf-token', csrf);
    // The API rejects a JSON content type with no body, so only declare one when sending one.
    if (init.body !== undefined && init.body !== null) {
      headers.set('content-type', 'application/json');
    }
  }

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    method,
    headers,
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    /*
     * The API reports failures as `error`; Fastify's own validation failures use
     * `message`. Both are read, because checking only one turned "Invalid email or
     * password" into "Request failed with 401" - a message that tells the person
     * signing in nothing about what to do next.
     */
    const stated =
      typeof body === 'object' && body !== null
        ? ((body as Record<string, unknown>)['error'] ??
          (body as Record<string, unknown>)['message'])
        : undefined;

    throw new ApiError(
      typeof stated === 'string' && stated !== ''
        ? stated
        : `Request failed with ${String(response.status)}`,
      response.status,
    );
  }

  return body as T;
}

/**
 * Upload a file via multipart/form-data. Used for spreadsheet imports.
 * Does NOT set content-type (the browser sets the boundary automatically).
 */
async function uploadFile<T>(
  path: string,
  file: File,
  params: Record<string, string> = {},
): Promise<T> {
  const headers = new Headers();
  const csrf = readCookie('rmc_csrf');
  if (csrf !== null) headers.set('x-csrf-token', csrf);

  const queryString = new URLSearchParams(params).toString();
  const url = `${BASE}${path}${queryString ? `?${queryString}` : ''}`;

  const form = new FormData();
  form.append('file', file);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'same-origin',
  });

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const stated =
      typeof body === 'object' && body !== null
        ? ((body as Record<string, unknown>)['error'] ??
          (body as Record<string, unknown>)['message'])
        : undefined;

    throw new ApiError(
      typeof stated === 'string' && stated !== ''
        ? stated
        : `Upload failed with ${String(response.status)}`,
      response.status,
    );
  }

  return body as T;
}

export interface AdminUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
  readonly isActive: boolean;
  readonly lastLoginAt: string | null;
  readonly failedLoginCount: number;
  readonly lockedUntil: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly workerId?: string | null;
  readonly workerName?: string | null;
  readonly roleEffectiveDate?: string | null;
  readonly inactiveDate?: string | null;
}

// ── Integration config types ─────────────────────────────────────────────

export interface IntegrationProviderInfo {
  readonly configured: boolean;
  readonly keys: readonly string[];
}

export interface IntegrationProviders {
  readonly oorwin: IntegrationProviderInfo;
  readonly adp: IntegrationProviderInfo;
}

export interface IntegrationConfigEntry {
  readonly key: string;
  readonly value: string;
  readonly isSensitive: boolean;
  readonly updatedAt: string;
}

export interface IntegrationConfigResponse {
  readonly provider: string;
  readonly entries: readonly IntegrationConfigEntry[];
}

export interface IntegrationTestResult {
  readonly provider: string;
  readonly status: 'connected' | 'error' | 'not_configured';
  readonly message: string;
}

// ── Ingestion types ──────────────────────────────────────────────────────

export interface IngestionResult {
  readonly source: string;
  readonly payPeriodId: string;
  readonly created: number;
  readonly updated: number;
  readonly unchanged: number;
  readonly skipped: number;
  readonly conflicts: readonly IngestionConflict[];
  readonly errors: readonly IngestionEntryError[];
}

export interface IngestionConflict {
  readonly workerRef: string;
  readonly workDate: string;
  readonly existingSource: string;
  readonly existingHours: string;
  readonly incomingHours: string;
  readonly reason: string;
}

export interface IngestionEntryError {
  readonly row?: number;
  readonly field: string;
  readonly value: string;
  readonly message: string;
}

export interface IngestionRun {
  readonly id: string;
  readonly source: string;
  readonly status: string;
  readonly records_created: number;
  readonly records_updated: number;
  readonly records_skipped: number;
  readonly records_conflicted: number;
  readonly started_at: string;
  readonly completed_at: string | null;
}

// ── Oorwin sync types ────────────────────────────────────────────────────

// Mirrors the Oorwin sync types in packages/domain/src/ingestion.ts.

export type OorwinSyncRequestOutcome =
  | { readonly outcome: 'started'; readonly syncId: string }
  | { readonly outcome: 'already_running'; readonly syncId: string }
  | { readonly outcome: 'skipped'; readonly reason: string };

export interface OorwinSyncRun {
  readonly syncId: string;
  readonly trigger: 'manual' | 'login' | 'initial' | 'backfill';
  readonly status: 'running' | 'completed' | 'no_change' | 'partial' | 'failed';
  readonly mode: 'incremental' | 'range' | null;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly phase: string | null;
  readonly current: number;
  readonly total: number;
  readonly rowsFetched: number;
  readonly rowsNew: number;
  readonly rowsChanged: number;
  readonly rowsUnchanged: number;
  readonly rowsRechecked: number;
  readonly rowsAwaitingApproval: number;
  readonly exceptionsRaised: number;
  readonly periodsAffected: readonly string[];
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly errorMessage: string | null;
}

export interface OorwinBackfillProgress {
  readonly backfillId: string;
  readonly status: 'running' | 'completed' | 'failed';
  readonly fromDate: string;
  readonly toDate: string;
  readonly chunksTotal: number;
  readonly chunksCompleted: number;
  readonly rowsFetched: number;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly errorMessage: string | null;
}

export type OorwinBackfillRequestOutcome =
  | { readonly outcome: 'started' | 'resumed' | 'already_running'; readonly backfillId: string }
  | { readonly outcome: 'busy'; readonly reason: string };

export interface OorwinSyncStatusResponse {
  readonly lastSync: OorwinSyncRun | null;
  readonly currentSync: OorwinSyncRun | null;
  readonly backfill: OorwinBackfillProgress | null;
  readonly knownRowCount: number;
  readonly openExceptionCount: number;
}

/** Mirrors PersistedException in packages/domain/src/exceptions.ts. */
export interface PersistedException {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly severity: 'info' | 'warning' | 'error' | 'blocker';
  readonly status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  readonly message: string;
  readonly payPeriodId: string | null;
  readonly assignmentId: string | null;
  readonly details: Readonly<Record<string, unknown>> | null;
  readonly createdAt: string;
}

export interface OpenPeriod {
  readonly id: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly week_ending: string;
  readonly status: string;
}

export interface ContractorWeek {
  readonly assignmentId: string;
  readonly payPeriodId: string;
  readonly periodStatus: string;
  readonly expenseId: string | null;
  readonly weekEnding: string;
  readonly payRate: string | null;
  readonly loadFactor: string | null;
  readonly billRate: string;
  readonly loadedCostPerHour: string | null;
  readonly grossProfitPerHour: string;
  readonly hours: string;
  readonly expenses: string;
  readonly subVendorCost: string;
  readonly grossProfit: string;
  readonly billed: string;
}

export interface RateSegment {
  readonly payRate: string;
  readonly loadFactor: string;
  readonly billRate: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
}

export interface ContractorDetail {
  readonly id: string;
  readonly name: string;
  readonly workerType: string;
  readonly clientName: string;
  readonly attributionSource: string;
  readonly assignmentId: string;
  readonly assignmentStart: string | null;
  readonly assignmentEnd: string | null;
  readonly assignmentStatus: string;
  readonly holidayDays: number | null;
  readonly vacationDays: number | null;
  readonly weeks: readonly ContractorWeek[];
  readonly rates: readonly RateSegment[];
  readonly verificationNotes?: readonly {
    readonly weekEnding: string;
    readonly status: string;
    readonly note: string | null;
    readonly verifiedBy: string | null;
    readonly verifiedAt: string | null;
  }[];
}

export interface ClientContractor {
  readonly id: string;
  readonly name: string;
  readonly workerType: string;
  readonly attributionSource: string;
  readonly weeks: number;
  readonly hours: string;
  readonly billed: string;
  readonly grossProfit: string;
  readonly grossProfitPerHour: string | null;
}

export interface ClientDetail {
  readonly id: string;
  readonly name: string;
  readonly paymentTerms: string | null;
  readonly contractors: readonly ClientContractor[];
  readonly trend: readonly TrendPoint[];
  readonly invoices: readonly {
    readonly id: string;
    readonly invoiceNumber: string;
    readonly issueDate: string;
    readonly dueDate: string | null;
    readonly amount: string;
    readonly status: string;
    readonly paidAmount: string;
  }[];
  readonly arSummary: {
    readonly totalOutstanding: string;
    readonly current: string;
    readonly thirtyDays: string;
    readonly sixtyDays: string;
    readonly ninetyPlus: string;
  };
}

export interface RecruiterContractor {
  readonly id: string;
  readonly name: string;
  readonly workerType: string;
  readonly clientName: string;
  readonly share: string;
  readonly weeks: number;
  readonly hours: string;
  readonly billed: string;
  readonly grossProfit: string;
  readonly creditedGp: string;
  readonly grossProfitPerHour: string | null;
}

export interface RecruiterWeekDetail {
  readonly weekEnding: string;
  readonly hours: string;
  readonly grossProfit: string;
  readonly creditedGp: string;
  readonly billed: string;
}

export interface TierInfo {
  readonly threshold: string;
  readonly rate: string;
  readonly planName: string;
  readonly tierMode: string;
}

export interface RecruiterSchedule {
  readonly name: string;
  readonly isElevated: boolean;
  readonly rates: readonly string[];
}

export interface RecruiterDetail {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly roleEffectiveDate: string | null;
  readonly planName: string | null;
  readonly commission: string;
  readonly contractors: readonly RecruiterContractor[];
  readonly tiers: readonly { readonly threshold: string; readonly rate: string }[];
  readonly tierMode: string;
  readonly elevatedPeriodMonths: number;
  readonly schedules: readonly RecruiterSchedule[];
  readonly trend: readonly {
    readonly weekEnding: string;
    readonly grossProfit: string;
    readonly hours: string;
    readonly contractors: number;
  }[];
}

export interface PipelineStage {
  readonly id: string;
  readonly label: string;
  readonly status: 'complete' | 'in_progress' | 'pending';
  readonly count: number;
  readonly total: number;
  readonly detail?: string;
}

export interface PipelineStatus {
  readonly periodId: string | null;
  readonly payrollRunId?: string | null;
  readonly periodLabel: string;
  readonly periodStart?: string;
  readonly periodEnd?: string;
  readonly payDate?: string;
  readonly verification?: {
    readonly total: number;
    readonly verified: number;
    readonly pending: number;
    readonly flagged: number;
    readonly rejected: number;
  };
  readonly hoursSummary?: {
    readonly regular: string;
    readonly overtime: string;
    readonly holiday: string;
    readonly vacation: string;
    readonly total: string;
    readonly priorTotal: string | null;
  };
  readonly stages: readonly PipelineStage[];
}

export interface PeriodQueueItem {
  readonly id: string;
  readonly weekEnding: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly isOverdue: boolean;
  readonly daysOverdue: number;
  readonly stagesComplete: number;
  readonly stagesTotal: number;
  readonly gpCount: number;
  readonly verifiedCount: number;
}

export interface PeriodQueueResponse {
  readonly activePeriods: readonly PeriodQueueItem[];
  readonly recentHistory: readonly PeriodQueueItem[];
  readonly recommendedPeriodId: string | null;
}

export interface ExceptionItem {
  readonly id: string;
  readonly type: string;
  readonly severity: 'high' | 'medium' | 'low';
  readonly title: string;
  readonly detail: string;
  readonly entityId?: string;
  readonly entityType?: string;
  readonly email?: string | null;
  readonly phone?: string | null;
  readonly clientName?: string;
  /** A recorded exception, which stays until someone resolves it with a note. */
  readonly resolvable?: boolean;
}

export interface CollectStageContractor {
  readonly id: string;
  readonly name: string;
  readonly clientName: string;
  readonly workerType: string;
  readonly weekEnding: string | null;
  readonly hoursSubmitted: string | null;
  readonly source: string | null;
  readonly status: 'received' | 'missing';
  readonly email: string | null;
  readonly phone: string | null;
  readonly lastSubmitted: string | null;
}

export interface ApproveStageData {
  readonly period: { readonly weekEnding: string; readonly periodLabel: string };
  readonly periodStatus: string;
  readonly current: {
    readonly totalGrossPay: string;
    readonly totalHours: string;
    readonly contractorCount: number;
  };
  readonly prior: {
    readonly totalGrossPay: string;
    readonly totalHours: string;
    readonly contractorCount: number;
  } | null;
  readonly checklist: {
    readonly hoursCollected: boolean;
    readonly hoursVerified: boolean;
    readonly gpCalculated: boolean;
    readonly commissionCalculated: boolean;
  };
}

export interface SubmitStageData {
  readonly period: { readonly weekEnding: string; readonly periodLabel: string };
  readonly periodStatus: string;
  readonly contractorCount: number;
  readonly idempotencyKey: string;
  readonly submissionStatus: 'pending' | 'submitted' | 'confirmed';
}

export interface ReconcileResultRow {
  readonly id: string;
  readonly workerName: string;
  readonly clientName: string;
  readonly sourceHours: string;
  readonly sourcePay: string;
  readonly adpHours: string | null;
  readonly adpPay: string | null;
  readonly status: 'matched' | 'discrepancy' | 'acknowledged';
}

export interface ReconcileStageData {
  readonly period: { readonly weekEnding: string; readonly periodLabel: string };
  readonly periodStatus: string;
  readonly contractorCount: number;
  readonly matched: number;
  readonly discrepancies: number;
  readonly unacknowledged: number;
  readonly matchRate: string;
  readonly reconciled: boolean;
  readonly results: readonly ReconcileResultRow[];
}

export interface VerifyWorkerDetail {
  readonly worker: {
    readonly id: string;
    readonly name: string;
    readonly email: string | null;
    readonly phone: string | null;
    readonly workerType: string;
  };
  readonly client: string;
  readonly assignmentStart: string | null;
  readonly currentPeriod: {
    readonly hours: string;
    readonly payRate: string;
    readonly billRate: string;
    readonly grossProfit: string;
  } | null;
  readonly dailyBreakdown: readonly {
    readonly id: string;
    readonly date: string;
    readonly hours: string;
    readonly earningType: string;
    readonly originalHours: string | null;
    readonly adjustmentReason: string | null;
    readonly adjustedAt: string | null;
  }[];
  readonly hasAdjustments: boolean;
  readonly verificationNotes?: readonly {
    readonly weekEnding: string;
    readonly status: string;
    readonly note: string | null;
    readonly verifiedBy: string | null;
    readonly verifiedAt: string | null;
  }[];
  readonly history: readonly {
    readonly weekEnding: string;
    readonly hours: string;
  }[];
  readonly ptoBalance: string | null;
}

export interface CollectStageData {
  readonly period: {
    readonly periodId: string;
    readonly weekEnding: string;
    readonly periodLabel: string;
  };
  readonly contractors: readonly CollectStageContractor[];
  readonly summary: { readonly received: number; readonly missing: number; readonly total: number };
}

export interface VerifyStageContractor {
  readonly id: string;
  readonly name: string;
  readonly clientName: string;
  readonly weekEnding: string;
  readonly payPeriodId: string;
  readonly regular: string;
  readonly overtime: string;
  readonly holiday: string;
  readonly vacation: string;
  readonly totalHours: string;
  readonly flagged: boolean;
  readonly flagReason: string | null;
  readonly verificationStatus:
    'pending' | 'auto_verified' | 'verified' | 'verified_with_adjustments' | 'rejected';
  /** Present when this row includes unapproved portal submissions. */
  readonly submissionStatus?: 'approved' | 'pending';
  /** IDs of the pending contractor_time_submissions rows. */
  readonly submissionIds?: string[];
  /** Whether any pending submission has a screenshot attached. */
  readonly hasScreenshot?: boolean;
}

export interface VerifyStageData {
  readonly period: { readonly periodLabel: string; readonly payrollRunId?: string };
  readonly contractors: readonly VerifyStageContractor[];
  readonly summary: {
    readonly total: number;
    readonly verified: number;
    readonly pending: number;
    readonly flagged: number;
    readonly rejected: number;
  };
}

export interface GpStageRow {
  readonly id: string;
  readonly name: string;
  readonly clientName: string;
  readonly workerType: string;
  readonly payRate: string;
  readonly loadFactor: string;
  readonly loadedCostPerHour: string;
  readonly billRate: string;
  readonly grossProfitPerHour: string;
  readonly hours: string;
  readonly expenses: string;
  readonly subVendorCost: string;
  readonly grossProfit: string;
  readonly billed: string;
}

export interface GpStageData {
  readonly period: { readonly weekEnding: string; readonly periodLabel: string };
  readonly rows: readonly GpStageRow[];
  readonly totals: {
    readonly hours: string;
    readonly expenses: string;
    readonly subVendorCost: string;
    readonly grossProfit: string;
    readonly billed: string;
    readonly loadedCost: string;
  };
}

export interface CommissionRecruiterContractor {
  readonly id: string;
  readonly name: string;
  readonly clientName: string;
  readonly grossProfit: string;
  readonly share: string;
}

export interface CommissionRecruiter {
  readonly id: string;
  readonly name: string;
  readonly totalGp: string;
  readonly commissionEarned: string;
  readonly effectiveRate: string;
  readonly tierReached: string;
  readonly contractors: readonly CommissionRecruiterContractor[];
}

export interface CommissionStageData {
  readonly period: { readonly weekEnding: string; readonly periodLabel: string };
  readonly recruiters: readonly CommissionRecruiter[];
  readonly tiers: readonly TierInfo[];
  readonly totals: { readonly totalGp: string; readonly totalCommission: string };
}

export interface DocPage {
  readonly slug: string;
  readonly title: string;
}

export interface DocSection {
  readonly heading: string;
  readonly content: string;
}

export interface DocContent {
  readonly slug: string;
  readonly title: string;
  readonly content?: string;
  readonly sections?: readonly DocSection[];
}

export interface PayrollEarningBreakdown {
  readonly category: string;
  readonly label: string;
  readonly hours: string;
  readonly pay: string;
}

export interface PayrollPreviewLine {
  readonly workerId: string;
  readonly workerName: string;
  readonly workerType: string;
  readonly adpCompanyCode: string | null;
  readonly adpFileNumber: string | null;
  readonly clientName: string;
  readonly totalHours: string;
  readonly totalPay: string;
  readonly payRate: string;
  readonly priorHours: string | null;
  readonly deltaPct: string | null;
  readonly earningBreakdown: readonly PayrollEarningBreakdown[];
  readonly warnings: readonly string[];
}

export interface PayrollPreview {
  readonly period: {
    readonly weekEnding: string;
    readonly periodStart: string;
    readonly periodEnd: string;
    readonly status: string;
  };
  readonly lines: readonly PayrollPreviewLine[];
  readonly totals: {
    readonly totalHours: string;
    readonly billableHours: string;
    readonly payableHours: string;
    readonly totalPay: string;
  };
  readonly summary: {
    readonly totalPayroll: string;
    readonly totalHours: string;
    readonly contractorCount: number;
    readonly priorTotalPayroll: string | null;
    readonly deltaPct: string | null;
  };
}

// ── Commission Credits types ──────────────────────────────────────────

export interface CreditEntry {
  readonly userId: string;
  readonly userName: string;
  readonly share: string;
  readonly commissionPlanId: string | null;
  readonly commissionPlanName: string | null;
  readonly schedule: string | null;
  readonly roleEffectiveDate: string | null;
}

export interface CreditWeek {
  readonly payPeriodId: string;
  readonly weekEnding: string;
  readonly label: string;
}

export interface CreditSave {
  readonly userId: string;
  readonly share: string;
  readonly lane: 'recruiting' | 'sales';
  readonly commissionPlanId?: string | null;
}

export interface CreditAssignment {
  readonly assignmentId: string;
  readonly workerId: string;
  readonly workerName: string;
  readonly clientName: string;
  readonly status: string;
  readonly workerType: string | null;
  readonly billRate: string | null;
  readonly payRate: string | null;
  readonly loadFactor: string | null;
  readonly periodGp: string | null;
  readonly periodHours: string | null;
  readonly engagementStart: string | null;
  readonly recruiting: CreditEntry[];
  readonly sales: CreditEntry[];
}

export interface BurdenConfigData {
  readonly id: string;
  readonly baseBurdenPct: string;
  readonly perHolidayDayFactor: string;
  readonly perVacationDayFactor: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
}

export const api = {
  async login(
    email: string,
    password: string,
  ): Promise<{ user: SessionUser; csrfToken: string; expiresAt: string }> {
    return request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  },
  async logout(): Promise<void> {
    await request('/auth/logout', { method: 'POST' });
  },
  async me(): Promise<Session> {
    return request('/auth/me');
  },
  async summary(range?: DateRange): Promise<Summary> {
    return request(`/api/dashboard/summary${dateQuery(range)}`);
  },
  async trend(range?: DateRange): Promise<TrendPoint[]> {
    return request(`/api/dashboard/trend${dateQuery(range)}`);
  },
  async workforceComposition(): Promise<WorkforceComposition> {
    return request('/api/dashboard/workforce-composition');
  },
  async clients(range?: DateRange): Promise<ClientRow[]> {
    return request(`/api/dashboard/clients${dateQuery(range)}`);
  },
  async contractors(range?: DateRange): Promise<ContractorRow[]> {
    return request(`/api/dashboard/contractors${dateQuery(range)}`);
  },
  async recruiters(range?: DateRange): Promise<RecruiterRow[]> {
    return request(`/api/dashboard/recruiters${dateQuery(range)}`);
  },
  async tiers(): Promise<TierInfo[]> {
    return request('/api/dashboard/tiers');
  },
  async contractor(id: string): Promise<ContractorDetail> {
    return request(`/api/dashboard/contractor/${id}`);
  },
  async addRate(
    contractorId: string,
    body: {
      assignmentId: string;
      effectiveFrom: string;
      payRate: string;
      billRate: string;
      loadFactor: string;
      subVendorRate?: string | undefined;
    },
  ): Promise<{ success: boolean }> {
    return request(`/api/dashboard/contractor/${contractorId}/rate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async client(id: string): Promise<ClientDetail> {
    return request(`/api/dashboard/client/${id}`);
  },
  async recruiter(id: string, dateRange?: DateRange, planId?: string): Promise<RecruiterDetail> {
    const parts: string[] = [];
    if (dateRange) {
      parts.push(`from=${dateRange.from}`, `to=${dateRange.to}`);
    }
    if (planId) parts.push(`planId=${planId}`);
    const qs = parts.length > 0 ? `?${parts.join('&')}` : '';
    return request(`/api/dashboard/recruiter/${id}${qs}`);
  },
  async recruiterWeeks(
    userId: string,
    workerId: string,
    dateRange?: DateRange,
    planId?: string,
  ): Promise<RecruiterWeekDetail[]> {
    const parts: string[] = [];
    if (dateRange) {
      parts.push(`from=${dateRange.from}`, `to=${dateRange.to}`);
    }
    if (planId) parts.push(`planId=${planId}`);
    const qs = parts.length > 0 ? `?${parts.join('&')}` : '';
    return request(`/api/dashboard/recruiter/${userId}/weeks/${workerId}${qs}`);
  },
  async docPages(): Promise<{ pages: DocPage[] }> {
    return request('/api/docs');
  },
  async docContent(slug: string): Promise<DocContent> {
    return request(`/api/docs/${slug}`);
  },
  async periodQueue(): Promise<PeriodQueueResponse> {
    return request('/api/dashboard/period-queue');
  },
  async pipeline(periodId?: string): Promise<PipelineStatus> {
    return request(`/api/dashboard/pipeline${pq(periodId)}`);
  },
  async exceptions(periodId?: string): Promise<{ exceptions: ExceptionItem[] }> {
    return request(`/api/dashboard/exceptions${pq(periodId)}`);
  },
  async stageCollect(periodId?: string): Promise<CollectStageData> {
    return request(`/api/dashboard/pipeline/collect${pq(periodId)}`);
  },
  async stageVerify(periodId?: string): Promise<VerifyStageData> {
    return request(`/api/dashboard/pipeline/verify${pq(periodId)}`);
  },
  async verifyWorkerDetail(workerId: string, periodId?: string): Promise<VerifyWorkerDetail> {
    return request(`/api/dashboard/pipeline/verify/${workerId}${pq(periodId)}`);
  },
  // adjustHours removed — hours are corrected in Oorwin (source of truth)
  async dailyHours(
    workerId: string,
    weekEnding: string,
    force?: boolean,
  ): Promise<{
    days: readonly {
      date: string;
      dayName: string;
      regular: string;
      overtime: string;
      holiday: string;
      vacation: string;
      total: string;
    }[];
    cached: boolean;
  }> {
    const params = new URLSearchParams({ weekEnding });
    if (force) params.set('force', 'true');
    return request(`/api/dashboard/pipeline/verify/${workerId}/daily?${params.toString()}`);
  },

  async stageGp(periodId?: string): Promise<GpStageData> {
    return request(`/api/dashboard/pipeline/calculate-gp${pq(periodId)}`);
  },
  async stageCommission(periodId?: string): Promise<CommissionStageData> {
    return request(`/api/dashboard/pipeline/calculate-commission${pq(periodId)}`);
  },
  async payrollPreview(periodId?: string, opts?: { scope?: 'all' }): Promise<PayrollPreview> {
    const params = new URLSearchParams();
    if (periodId) params.set('periodId', periodId);
    if (opts?.scope) params.set('scope', opts.scope);
    const qs = params.toString();
    return request(`/api/dashboard/payroll-preview${qs ? `?${qs}` : ''}`);
  },
  async stageApprove(periodId?: string): Promise<ApproveStageData> {
    return request(`/api/dashboard/pipeline/approve${pq(periodId)}`);
  },
  async stageSubmit(periodId?: string): Promise<SubmitStageData> {
    return request(`/api/dashboard/pipeline/submit${pq(periodId)}`);
  },
  async stageReconcile(periodId?: string): Promise<ReconcileStageData> {
    return request(`/api/dashboard/pipeline/reconcile${pq(periodId)}`);
  },
  async batchVerify(
    workerIds: string[],
    status: 'verified' | 'rejected' | 'auto_verified' | 'verified_with_adjustments',
    periodId?: string,
    note?: string,
  ): Promise<{ updated: number }> {
    return request(`/api/dashboard/pipeline/verify${pq(periodId)}`, {
      method: 'POST',
      body: JSON.stringify({ workerIds, status, periodId, ...(note ? { note } : {}) }),
    });
  },
  async lockPayroll(periodId?: string): Promise<{ success: boolean; status: string }> {
    return request(`/api/dashboard/pipeline/lock-payroll${pq(periodId)}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async approveBatch(periodId?: string): Promise<{ success: boolean; status: string }> {
    return request(`/api/dashboard/pipeline/approve${pq(periodId)}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async submitBatch(periodId?: string): Promise<{ success: boolean; status: string }> {
    return request(`/api/dashboard/pipeline/submit${pq(periodId)}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async acknowledgeDiscrepancies(resultIds: string[]): Promise<{ acknowledged: number }> {
    return request('/api/dashboard/pipeline/reconcile/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ resultIds }),
    });
  },
  async closePeriod(periodId?: string): Promise<{ success: boolean; status: string }> {
    return request(`/api/dashboard/pipeline/reconcile/close${pq(periodId)}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  // ── Commission management ────────────────────────────────────────────

  async getCommissionTiers(): Promise<{
    plan: { id: string; name: string; tierMode: string } | null;
    tiers: {
      id: string;
      threshold: string;
      rate: string;
      effectiveFrom: string;
      effectiveTo: string | null;
    }[];
  }> {
    return request('/api/admin/commission/tiers');
  },
  async saveCommissionTiers(body: {
    tiers: { threshold: string; rate: string }[];
    tierMode: string;
  }): Promise<{ success: boolean }> {
    return request('/api/admin/commission/tiers', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  async getCommissionAllocations(): Promise<{
    allocations: {
      id: string;
      assignmentId: string;
      workerName: string;
      clientName: string;
      creditedUserName: string;
      share: string;
      effectiveFrom: string;
      effectiveTo: string | null;
    }[];
  }> {
    return request('/api/admin/commission/allocations');
  },

  // ── Commission Plans (multi-plan, multi-schedule) ───────────────────

  async getCommissionPlans(): Promise<{
    plans: CommissionPlan[];
  }> {
    return request('/api/admin/commission/plans');
  },

  async getCommissionPlanOptions(): Promise<{
    plans: { id: string; name: string; elevatedPeriodMonths: number }[];
  }> {
    return request('/api/admin/commission/plans/options');
  },

  async createCommissionPlan(body: {
    name: string;
    tierMode: string;
    elevatedPeriodMonths?: number;
  }): Promise<{ id: string }> {
    return request('/api/admin/commission/plans', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async updateCommissionPlan(
    planId: string,
    body: { name?: string; tierMode?: string; elevatedPeriodMonths?: number },
  ): Promise<{ success: boolean }> {
    return request(`/api/admin/commission/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async updatePlanBrackets(
    planId: string,
    brackets: { threshold: string }[],
  ): Promise<{ success: boolean }> {
    return request(`/api/admin/commission/plans/${planId}/brackets`, {
      method: 'PUT',
      body: JSON.stringify({ brackets }),
    });
  },

  async createSchedule(
    planId: string,
    body: { name: string; fillPriority: number; isElevated: boolean },
  ): Promise<{ id: string }> {
    return request(`/api/admin/commission/plans/${planId}/schedules`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async updateSchedule(
    planId: string,
    scheduleId: string,
    body: { name?: string; fillPriority?: number; isElevated?: boolean },
  ): Promise<{ success: boolean }> {
    return request(`/api/admin/commission/plans/${planId}/schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async updateScheduleRates(
    planId: string,
    scheduleId: string,
    rates: { tierId: string; rate: string }[],
  ): Promise<{ success: boolean }> {
    return request(`/api/admin/commission/plans/${planId}/schedules/${scheduleId}/rates`, {
      method: 'PUT',
      body: JSON.stringify({ rates }),
    });
  },

  async deleteCommissionPlan(planId: string): Promise<void> {
    await request(`/api/admin/commission/plans/${planId}`, {
      method: 'DELETE',
    });
  },

  // ── My Commission (recruiter self-service) ───────────────────────────

  async myCommission(range?: DateRange): Promise<MyCommissionData> {
    return request(`/api/dashboard/my-commission${dateQuery(range)}`);
  },

  // ── Weekly Commission Report ────────────────────────────────────────

  async weeklyCommission(weekEnding?: string): Promise<WeeklyCommissionData> {
    return request(
      `/api/dashboard/weekly-commission${weekEnding ? `?weekEnding=${weekEnding}` : ''}`,
    );
  },

  // ── Reports ───────────────────────────────────────────────────────

  async gpSummary(weekEnding?: string): Promise<GpSummaryData> {
    return request(`/api/reports/gp-summary${weekEnding ? `?weekEnding=${weekEnding}` : ''}`);
  },
  async weeklyScoreboard(weekEnding?: string): Promise<WeeklyScoreboardData> {
    return request(`/api/reports/leaderboard${weekEnding ? `?weekEnding=${weekEnding}` : ''}`);
  },
  async goalPortal(query: {
    subject: 'company' | 'recruiter';
    year?: number | undefined;
    weekEnding?: string | undefined;
    userId?: string | undefined;
  }): Promise<GoalPortalData> {
    const params = new URLSearchParams({ subject: query.subject });
    if (query.year !== undefined) params.set('year', String(query.year));
    if (query.weekEnding !== undefined) params.set('weekEnding', query.weekEnding);
    if (query.userId !== undefined) params.set('userId', query.userId);
    return request(`/api/reports/goals?${params.toString()}`);
  },
  async saveScoreboardNote(body: {
    weekEnding: string;
    body: string;
    kind?: string;
  }): Promise<{ id: string }> {
    return request('/api/reports/scoreboard-notes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async listGoalsAdmin(year: number): Promise<{
    year: number;
    company: {
      annual_amount: string;
      regular_weekly_start: string;
      weekly_increment: string;
      holiday_default_amount: string | null;
    } | null;
    people: {
      user_id: string;
      user_name: string;
      role: string;
      annual_amount: string;
      regular_weekly_start: string;
      weekly_increment: string;
    }[];
    recruiters: { id: string; full_name: string; role: string }[];
  }> {
    return request(`/api/admin/goals?year=${String(year)}`);
  },
  async saveGoalsAdmin(body: {
    year: number;
    company?: {
      annualAmount: string;
      regularWeeklyStart: string;
      weeklyIncrement: string;
      holidayDefault?: string;
    };
    people?: readonly {
      userId: string;
      annualAmount: string;
      regularWeeklyStart: string;
      weeklyIncrement: string;
      holidayDefault?: string;
    }[];
  }): Promise<{ ok: boolean }> {
    return request('/api/admin/goals', { method: 'PUT', body: JSON.stringify(body) });
  },
  async expensesReport(weekEnding?: string): Promise<ExpensesReportData> {
    return request(`/api/reports/expenses${weekEnding ? `?weekEnding=${weekEnding}` : ''}`);
  },
  async saveExpense(body: {
    assignmentId: string;
    payPeriodId: string;
    amount: string;
    description?: string;
  }): Promise<{ ok: boolean }> {
    return request('/api/reports/expenses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async deleteExpense(id: string): Promise<{ ok: boolean }> {
    return request(`/api/reports/expenses/${id}`, { method: 'DELETE' });
  },

  // ── Contractor portal ────────────────────────────────────────────────

  async portalMyPeriods(): Promise<{
    periods: {
      id: string;
      weekEnding: string;
      periodStart: string;
      periodEnd: string;
      status: string;
    }[];
  }> {
    return request('/api/portal/my-periods');
  },
  async portalMyHours(periodId: string): Promise<{
    entries: {
      id: string;
      workDate: string;
      earningCategory: string;
      hours: string;
      status: string;
      hasScreenshot: boolean;
      rejectionNote: string | null;
    }[];
  }> {
    return request(`/api/portal/my-hours/${periodId}`);
  },
  async portalSubmitHours(body: {
    periodId: string;
    entries: { workDate: string; earningCategory: string; hours: number }[];
    screenshotKey?: string;
  }): Promise<{ success: boolean; submitted: number }> {
    return request('/api/portal/submit-hours', { method: 'POST', body: JSON.stringify(body) });
  },
  async portalUploadScreenshot(file: File): Promise<{ key: string }> {
    return uploadFile('/api/portal/upload', file, {});
  },
  async portalMySubmissions(): Promise<{
    submissions: {
      payPeriodId: string;
      weekEnding: string;
      status: string;
      entryCount: number;
      totalHours: string;
    }[];
  }> {
    return request('/api/portal/my-submissions');
  },

  // ── Contractor submission review ────────────────────────────────────

  async getContractorSubmissions(periodId?: string): Promise<{
    submissions: {
      id: string;
      workerName: string;
      clientName: string;
      workDate: string;
      earningCategory: string;
      hours: string;
      status: string;
      hasScreenshot: boolean;
      screenshotPath: string | null;
      submittedAt: string;
      weekEnding: string;
    }[];
  }> {
    return request(
      `/api/dashboard/contractor-submissions${periodId ? `?periodId=${periodId}` : ''}`,
    );
  },
  async approveSubmission(id: string): Promise<{ success: boolean }> {
    return request(`/api/dashboard/contractor-submissions/${id}/approve`, {
      method: 'POST',
      body: '{}',
    });
  },
  async rejectSubmission(id: string, note: string): Promise<{ success: boolean }> {
    return request(`/api/dashboard/contractor-submissions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  async bulkApproveSubmissions(ids: string[]): Promise<{ approved: number }> {
    return request('/api/dashboard/contractor-submissions/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ submissionIds: ids }),
    });
  },

  // ── Ingestion endpoints ──────────────────────────────────────────────

  async uploadTimesheet(file: File, payPeriodId: string): Promise<IngestionResult> {
    return uploadFile('/api/ingestion/upload', file, { payPeriodId });
  },

  /** Sync Oorwin timesheets now. Automatic syncs run at login, on the server. */
  async syncOorwinTimesheets(): Promise<OorwinSyncRequestOutcome> {
    return request('/api/ingestion/sync-oorwin', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async oorwinSyncStatus(): Promise<OorwinSyncStatusResponse> {
    return request('/api/ingestion/sync-status');
  },

  /** Load timesheet history from `fromDate`, or resume the latest unfinished load. */
  async loadOorwinHistory(fromDate?: string): Promise<OorwinBackfillRequestOutcome> {
    return request('/api/ingestion/oorwin/backfill', {
      method: 'POST',
      body: JSON.stringify(fromDate === undefined ? {} : { fromDate }),
    });
  },

  /** Open exceptions that are not tied to a single pay period. */
  async organizationExceptions(): Promise<{ exceptions: PersistedException[] }> {
    return request('/api/exceptions/organization');
  },

  async resolveException(id: string, note: string): Promise<{ resolved: true }> {
    return request(`/api/exceptions/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },

  async ingestionRuns(payPeriodId?: string): Promise<{ runs: IngestionRun[] }> {
    const query = payPeriodId ? `?payPeriodId=${encodeURIComponent(payPeriodId)}` : '';
    return request(`/api/ingestion/runs${query}`);
  },

  async openPeriods(): Promise<{ periods: OpenPeriod[]; defaultPeriodId: string | null }> {
    return request('/api/ingestion/periods');
  },

  // ── Admin endpoints ──────────────────────────────────────────────────

  async listUsers(): Promise<AdminUser[]> {
    return request('/api/admin/users');
  },
  async createUser(data: {
    email: string;
    fullName: string;
    role: string;
    password: string;
    workerId?: string;
  }): Promise<AdminUser> {
    return request('/api/admin/users', { method: 'POST', body: JSON.stringify(data) });
  },
  async getWorkerList(): Promise<{
    workers: {
      id: string;
      fullName: string;
      email: string | null;
      clientName: string;
      hasLoginAccess: boolean;
      hasUserAccount: boolean;
    }[];
  }> {
    return request('/api/admin/workers');
  },
  async updateUser(
    id: string,
    data: {
      role?: string;
      fullName?: string;
      isActive?: boolean;
      workerId?: string | null;
      roleEffectiveDate?: string | null;
      inactiveDate?: string | null;
    },
  ): Promise<AdminUser> {
    return request(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  async resetUserPassword(id: string, password: string): Promise<{ success: boolean }> {
    return request(`/api/admin/users/${encodeURIComponent(id)}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },
  async revokeUserSessions(id: string): Promise<{ success: boolean; revoked: number }> {
    return request(`/api/admin/users/${encodeURIComponent(id)}/sessions`, { method: 'DELETE' });
  },

  // ── Integration config endpoints ───────────────────────────────────

  async integrationStatus(): Promise<IntegrationProviders> {
    return request('/api/admin/integrations');
  },
  async integrationConfig(provider: string): Promise<IntegrationConfigResponse> {
    return request(`/api/admin/integrations/${encodeURIComponent(provider)}`);
  },
  async saveIntegrationConfig(
    provider: string,
    values: Record<string, string>,
  ): Promise<{ success: boolean }> {
    return request(`/api/admin/integrations/${encodeURIComponent(provider)}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    });
  },
  async testIntegration(provider: string): Promise<IntegrationTestResult> {
    return request(`/api/admin/integrations/${encodeURIComponent(provider)}/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async deleteIntegrationConfig(provider: string): Promise<{ success: boolean }> {
    return request(`/api/admin/integrations/${encodeURIComponent(provider)}`, {
      method: 'DELETE',
    });
  },

  // ── Account endpoints ────────────────────────────────────────────────

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean }> {
    return request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  // ── Available recruiters/salespeople for dropdowns ─────────────────

  async listAvailableRecruiters(): Promise<{ id: string; full_name: string; role: string }[]> {
    return request('/api/admin/users/recruiters');
  },

  // ── Period management ────────────────────────────────────────────────

  async listPeriodsAdmin(): Promise<{ periods: AdminPeriodRow[] }> {
    return request('/api/admin/periods');
  },

  async periodGaps(): Promise<{ gaps: PeriodGapRow[] }> {
    return request('/api/admin/periods/gaps');
  },

  async periodConfig(): Promise<PeriodConfigResponse> {
    return request('/api/admin/periods/config');
  },

  async updatePeriodConfig(changes: {
    readonly payWeekEndingDay?: number;
    readonly payrollGoLiveDate?: string | null;
    readonly verifyMinHours?: number;
    readonly verifyFlaggingEnabled?: boolean;
  }): Promise<PeriodConfigResponse> {
    return request('/api/admin/periods/config', {
      method: 'PUT',
      body: JSON.stringify(changes),
    });
  },

  async generatePeriods(count: number): Promise<{ periods: AdminPeriodRow[]; count: number }> {
    return request('/api/admin/periods/generate', {
      method: 'POST',
      body: JSON.stringify({ count }),
    });
  },

  async transitionPeriodStatus(id: string, status: string): Promise<AdminPeriodRow> {
    return request(`/api/admin/periods/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  async bulkClosePeriods(
    periodIds: string[],
  ): Promise<{ closed: number; skipped: number; errors: string[] }> {
    return request('/api/admin/periods/bulk-close', {
      method: 'POST',
      body: JSON.stringify({ periodIds }),
    });
  },

  async deletePeriod(id: string): Promise<void> {
    return request(`/api/admin/periods/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ── Sync management ──────────────────────────────────────────────────

  async overduePeriods(): Promise<OverduePeriodsResponse> {
    return request('/api/dashboard/overdue-periods');
  },

  async syncOorwin(): Promise<SyncResponse> {
    return request('/api/admin/sync/oorwin', { method: 'POST' });
  },

  async syncRuns(): Promise<{ runs: SyncRunRow[] }> {
    return request('/api/admin/sync/runs');
  },

  // ── Developer tools ──────────────────────────────────────────────────

  async adpCatalog(): Promise<{
    endpoints: readonly {
      path: string;
      method: string;
      name: string;
      group: 'read' | 'write';
      description: string;
      defaultQuery: Record<string, string>;
      defaultBody: Record<string, unknown>;
      fields: readonly { name: string; description: string; values?: string }[];
    }[];
  }> {
    return request('/api/admin/dev/adp-catalog');
  },

  async adpProxy(
    path: string,
    queryParams?: Record<string, string>,
  ): Promise<{
    ok: boolean;
    responseTimeMs: number;
    data: unknown;
    error?: string;
  }> {
    return request('/api/admin/dev/adp-proxy', {
      method: 'POST',
      body: JSON.stringify({ path, method: 'GET', queryParams }),
    });
  },

  async oorwinCatalog(): Promise<{
    endpoints: readonly {
      path: string;
      name: string;
      group: 'in_use' | 'discovery';
      description: string;
      defaultBody: Record<string, unknown>;
      fields: readonly { name: string; description: string; values?: string }[];
    }[];
  }> {
    return request('/api/admin/dev/oorwin-catalog');
  },

  async oorwinProxy(
    path: string,
    body: Record<string, unknown>,
  ): Promise<{
    ok: boolean;
    responseTimeMs: number;
    data: unknown;
    error?: string;
  }> {
    return request('/api/admin/dev/oorwin-proxy', {
      method: 'POST',
      body: JSON.stringify({ path, body }),
    });
  },

  // ── Commission Credits ───────────────────────────────────────────────

  async getCommissionCredits(
    search?: string,
    payPeriodId?: string,
    weekEnding?: string,
    weekScope?: '1' | '2' | 'both',
  ): Promise<{
    assignments: CreditAssignment[];
    weeks: CreditWeek[];
  }> {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (payPeriodId) params.set('payPeriodId', payPeriodId);
    if (weekEnding) params.set('weekEnding', weekEnding);
    if (weekScope) params.set('weekScope', weekScope);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request(`/api/admin/commission/credits${qs}`);
  },

  async saveCommissionCredits(
    assignmentId: string,
    credits: CreditSave[],
  ): Promise<{ success: boolean; count: number }> {
    return request(`/api/admin/commission/credits/${encodeURIComponent(assignmentId)}`, {
      method: 'PUT',
      body: JSON.stringify({ credits }),
    });
  },

  async updateAssignmentStart(
    assignmentId: string,
    startDate: string,
  ): Promise<{ success: boolean; assignmentId: string; startDate: string }> {
    return request(`/api/admin/assignments/${encodeURIComponent(assignmentId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ startDate }),
    });
  },

  // ── Burden Config ────────────────────────────────────────────────────

  async getBurdenConfig(): Promise<{ config: BurdenConfigData | null }> {
    return request('/api/admin/burden-config');
  },

  async saveBurdenConfig(body: {
    baseBurdenPct: string;
    perHolidayDayFactor: string;
    perVacationDayFactor: string;
  }): Promise<{ success: boolean }> {
    return request('/api/admin/burden-config', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  // ── Worker Benefits & Load Factor ────────────────────────────────────

  async updateWorkerBenefits(
    workerId: string,
    body: { holidayDays?: number | null; vacationDays?: number | null },
  ): Promise<{ id: string; holidayDays: number | null; vacationDays: number | null }> {
    return request(`/api/admin/workers/${encodeURIComponent(workerId)}/benefits`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async getWorkerLoadFactor(workerId: string): Promise<{
    loadFactor: string | null;
    reason?: string;
    holidayDays?: number | null;
    vacationDays?: number | null;
    baseBurdenPct?: string;
    perHolidayDayFactor?: string;
    perVacationDayFactor?: string;
  }> {
    return request(`/api/admin/workers/${encodeURIComponent(workerId)}/load-factor`);
  },
};
